import { useCallback, useEffect, useRef } from "react";

import { useDirtyFormContext } from "./DirtyFormContext";

/**
 * Runs an action — typically a navigation — once no registered form is dirty any more. A form that
 * was just saved or reset still counts as dirty for a render or two: it unregisters in an effect, and
 * NavigationBlocker's `useBlocker` takes the new state in an effect of its own. Navigating in the same
 * tick would therefore ask "leave page?" right after a successful save. The deferred action runs in the
 * commit where the context reports clean; NavigationBlocker is mounted before the routed page, so its
 * blocker has been updated by then.
 */
export function useRunWhenFormsClean(): (action: () => void) => void {
  const { isDirty } = useDirtyFormContext();
  const pending = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (isDirty || !pending.current) {
      return;
    }

    const action = pending.current;
    pending.current = null;
    action();
  }, [isDirty]);

  return useCallback(
    (action: () => void) => {
      if (isDirty) {
        pending.current = action;
      } else {
        action();
      }
    },
    [isDirty]
  );
}
